<template>
  <aha-context-menu
    ref="canvas-context-menu"
    :x="targetX"
    :y="targetY"
    :offsetX="offsetX"
    :offsetY="offsetY"
    :layerX="layerX"
    :layerY="layerY"
    :targetClick="targetClick"
    isMenuInCanvas
    :target="'slide-container'"
    class="canvas-right-click-menu"
  >
    <aha-context-menu-item
      @click="handlePasteBlock()"
      class="canvas-right-click-menu-item __undo"
      v-if="isContentSlideType"
      :disabled="!isPasteBlockEnabled"
      hasDivider
    >
      <div class="space-x-2">
        <aha-icon
          name="paste"
          class="text-base-70"
          width="24"
          height="24"
          viewBox="0 0 20 20"
        />
        <span>Paste</span>
      </div>
      <ContextMenuShortcutKeys keyName="v-key" />
    </aha-context-menu-item>
    <aha-context-menu-item
      v-if="isUndoRedoVisible"
      :disabled="isUndoDisabled"
      @click="handleUndoAction()"
      class="canvas-right-click-menu-item __undo"
    >
      <div class="space-x-2">
        <aha-icon
          name="undo"
          class="text-base-70"
          width="24"
          height="24"
          viewBox="0 0 16 16"
        />
        <span>Undo</span>
      </div>
      <ContextMenuShortcutKeys keyName="z-key" />
    </aha-context-menu-item>
    <aha-context-menu-item
      v-if="isUndoRedoVisible"
      :disabled="isRedoDisabled"
      @click="handleRedoAction()"
      class="canvas-right-click-menu-item __redo"
      hasDivider
    >
      <div class="space-x-2">
        <aha-icon
          name="redo"
          class="text-base-70"
          width="24"
          height="24"
          viewBox="0 0 16 16"
        />
        <span>Redo</span>
      </div>
      <ContextMenuShortcutKeys keyName="z-key" hasShiftKey />
    </aha-context-menu-item>
    <aha-context-menu-item
      class="canvas-right-click-menu-item"
      v-if="isRightClickAddImageVisible"
      @click="handleOpenAddImageModal()"
    >
      <aha-icon name="image" class="text-base-70" />
      <span>{{ addImageText }}</span>
    </aha-context-menu-item>
    <aha-context-menu-item
      class="canvas-right-click-menu-item"
      v-if="isRightClickAddAudioVisible"
      @click="handleOpenAudioSettingModal()"
      hasDivider
    >
      <aha-icon name="music-note" class="text-base-70" />
      <span>{{ addAudioText }}</span>
    </aha-context-menu-item>
    <aha-context-menu-item
      class="pb-3 canvas-right-click-menu-item"
      @click="redirectToCustomiseTab()"
    >
      <aha-icon
        name="paint"
        class="mb-1 text-base-70"
        width="22"
        height="22"
        viewBox="0 0 20 20"
      />
      <span>Change background</span>
    </aha-context-menu-item>
  </aha-context-menu>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import EventBus from '@/eventBus'
import utils from '@/util'
import gaCategories from '@/constant/gaCategories'
import ContextMenuShortcutKeys from './ContextMenuShortcutKeys.vue'
import { isFocusingOnAnInput } from '@/utils/dom.utils'

class SVGPathElement extends HTMLElement {}

window.SVGPathElement = SVGPathElement

export default {
  components: {
    ContextMenuShortcutKeys,
  },
  props: {
    targetX: {
      type: Number,
      default: null,
    },
    targetY: {
      type: Number,
      default: null,
    },
    offsetX: {
      type: Number,
      default: null,
    },
    offsetY: {
      type: Number,
      default: null,
    },
    layerX: {
      type: Number,
      default: null,
    },
    layerY: {
      type: Number,
      default: null,
    },
    targetClick: {
      type: [HTMLSpanElement, HTMLImageElement, HTMLDivElement, HTMLCanvasElement,
        SVGPathElement, HTMLElement, HTMLParagraphElement, SVGTextElement, SVGSVGElement],
      default: null,
    },
  },
  data() {
    return {
      gaLabel: 'Canvas > Right click > Open canvas context menu',
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      plan: (state) => state.plan,
      clipboardBlocks: (state) => state.clipboardBlocks,
    }),
    ...mapState([
      'undoHistory',
      'redoHistory',
      'isBlockHistory',
      'isSyncing',
      'isDisableEditPresentation',
    ]),
    ...mapGetters(['selectedSlide']),
    isRightClickAddImageVisible() {
      const slideTypes = ['freestyle', 'textSlide', 'bulletPoints', 'qrcode', 'youtube', 'document', 'googleSlides', 'leaderboard', 'spinnerWheel']
      return !slideTypes.includes(this.slideActive.type)
    },
    isRightClickAddAudioVisible() {
      const excludedSlideTypes = ['qrcode', 'youtube', 'googleSlides', 'spinnerWheel']
      return !excludedSlideTypes.includes(this.slideActive.type)
    },
    isUndoRedoVisible() {
      const excludedSlideTypes = ['qrcode', 'googleSlides']
      return !excludedSlideTypes.includes(this.slideActive.type)
    },
    isUndoDisabled() {
      return !this.undoHistory.length || this.isBlockHistory ||
        this.isSyncing !== 2 || this.isDisableEditPresentation
    },
    isRedoDisabled() {
      return !this.redoHistory.length || this.isBlockHistory ||
        this.isSyncing !== 2 || this.isDisableEditPresentation
    },
    addAudioText() {
      return this.selectedSlide.audioLink ? 'Change audio' : 'Add audio'
    },
    addImageText() {
      return this.slideActive.image ? 'Change image' : 'Add image'
    },
    isContentSlideType() {
      return this.slideActive.type === 'freestyle'
    },
    isPasteBlockEnabled() {
      if (isFocusingOnAnInput()) return false
      return this.clipboardBlocks &&
      this.clipboardBlocks.length > 0
    },
  },

  mounted() {
    EventBus.$on('canvas-right-click-menu: show', () => {
      this.$refs['canvas-context-menu'].show()
    })
    EventBus.$on('canvas-right-click-menu: hide', () => {
      this.$refs['canvas-context-menu'].hide()
    })
  },
  destroyed() {
    EventBus.$off('canvas-right-click-menu: show')
    EventBus.$off('canvas-right-click-menu: hide')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    ...mapActions([
      'undoAction',
      'redoAction',
    ]),
    handlePasteBlock() {
      EventBus.$emit('right-click-paste-block')
    },
    redirectToCustomiseTab() {
      this.updateSlideActiveAction({
        activeTab: 3,
      })

      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Change background',
      )
    },
    handleUndoAction() {
      this.undoAction()
      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Undo',
      )
    },
    handleRedoAction() {
      this.redoAction()
      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Redo',
      )
    },
    handleOpenAddImageModal() {
      this.updateSlideActiveAction({
        activeTab: 2,
      })
      setTimeout(() => {
        if (this.slideActive.type === 'image') {
          EventBus.$emit('open-add-image-modal')
        } else if (!this.slideActive.image) {
          EventBus.$emit('open-add-question-image-modal')
        } else {
          EventBus.$emit('open-edit-question-image-modal')
        }
      }, 250)
      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Add/ Change image',
      )
    },
    handleOpenAudioSettingModal() {
      this.updateSlideActiveAction({
        activeTab: 4,
      })
      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Add/ Change audio',
      )
      if (this.plan.group == 'free') return
      setTimeout(() => {
        EventBus.$emit('audio: open-audio-modal')
      }, 250)
    },
  },
}
</script>

<style lang="scss">
.canvas-right-click-menu {
  font-family: Nunito, sans-serif;
  .canvas-right-click-menu-item {
    display: flex !important;
    flex: none !important;
    font-size: 17px;
    padding: 0.5rem;
    &:last-child {
      padding-bottom: 0.25rem !important;
    }
    &.__undo, &.__redo {
      justify-content: space-between;
    }
  }
  &::v-deep .container-shortcut-key .shortcut-key {
    background: #CECECE;
  }
}
</style>
